import { IconBack, IconCard, IconLessons } from './icons';

/** two sections: the lessons and «Моя подписка» (03.11 review merged «Мой путь» and «Подписка») */
export type Section = 'lessons' | 'sub' | null;

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || 'FC';

/** FACECONTROL in tracked serif capitals with «Клуб Яны Чикало» under it — the one wordmark everywhere. */
export function Wordmark({ onClick, href }: { onClick?: () => void; href?: string }) {
  const inner = (
    <>
      <span className="wordmark__name">FACECONTROL</span>
      <span className="wordmark__club">Клуб Яны Чикало</span>
    </>
  );
  if (href) return <a className="wordmark" href={href} aria-label="FaceControl — на главную">{inner}</a>;
  return (
    <button type="button" className="wordmark" onClick={onClick} aria-label="FaceControl — к урокам">
      {inner}
    </button>
  );
}

interface MemberHeaderProps {
  active: Section;
  name: string;
  isWeb: boolean;
  /** lesson pages on a phone: «← Уроки» instead of the wordmark's left slot */
  back?: { label: string; onClick: () => void } | null;
  onLessons: () => void;
  onSubscription: () => void;
  onLogout?: () => void;
}

/** The member header: wordmark, two sections, the member's initials (→ «Моя подписка»). */
export function MemberHeader({ active, name, isWeb, back, onLessons, onSubscription, onLogout }: MemberHeaderProps) {
  const first = name.split(' ')[0] ?? name;
  return (
    <header className={`top${back ? ' top--back' : ''}`}>
      <div className="top__in">
        {back && (
          <button type="button" className="top__back" onClick={back.onClick}>
            <IconBack /> {back.label}
          </button>
        )}
        <Wordmark onClick={onLessons} />
        <nav className="top__nav" aria-label="Разделы клуба">
          <button type="button" className={active === 'lessons' ? 'is-on' : ''} aria-current={active === 'lessons' ? 'page' : undefined} onClick={onLessons}>
            Уроки
          </button>
          <button type="button" className={active === 'sub' ? 'is-on' : ''} aria-current={active === 'sub' ? 'page' : undefined} onClick={onSubscription}>
            Моя подписка
          </button>
        </nav>
        <div className="top__me">
          <button type="button" className="top__who" onClick={onSubscription} aria-label="Моя подписка">
            <span className="top__label">{first}</span>
            <span className="avatar" aria-hidden="true">
              {initials(name)}
            </span>
          </button>
          {isWeb && onLogout && (
            <button type="button" className="top__out" onClick={onLogout}>
              Выйти
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

/** Phones: the two sections at the bottom, 56 px tall, active one underlined in terracotta. */
export function BottomNav({ active, onLessons, onSubscription }: { active: Section; onLessons: () => void; onSubscription: () => void }) {
  return (
    <nav className="bnav" aria-label="Разделы клуба">
      <button type="button" className={active === 'lessons' ? 'is-on' : ''} aria-current={active === 'lessons' ? 'page' : undefined} onClick={onLessons}>
        <IconLessons />
        Уроки
      </button>
      <button type="button" className={active === 'sub' ? 'is-on' : ''} aria-current={active === 'sub' ? 'page' : undefined} onClick={onSubscription}>
        <IconCard />
        Моя подписка
      </button>
    </nav>
  );
}

export function Footer({ status, support }: { status?: string | null; support?: string | null }) {
  return (
    <footer className="foot">
      <div className="foot__in">
        <div className="wordmark wordmark--small" aria-hidden="true">
          <span className="wordmark__name">FACECONTROL</span>
          <span className="wordmark__club">Клуб Яны Чикало</span>
        </div>
        <span className="foot__rule" aria-hidden="true" />
        {status && (
          <span className="foot__status">
            <i aria-hidden="true" /> {status}
          </span>
        )}
        {support && (
          <a className="foot__link" href={support} target="_blank" rel="noopener">
            Поддержка
          </a>
        )}
      </div>
    </footer>
  );
}
